/**
 * Variant Shuffle - Shows product variants as separate shuffled items
 * Handles collection pages, search results, and product grids
 */

// Inject skeleton styles IMMEDIATELY when script loads (before DOMContentLoaded)
(function () {
  if (document.getElementById('variant-shuffle-styles')) return;
  const style = document.createElement('style');
  style.id = 'variant-shuffle-styles';
  style.textContent = `
    .variant-shuffle-skeleton .product-card__image-wrapper {
      position: relative;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.5s infinite;
    }
    .variant-shuffle-skeleton .product-card__image {
      aspect-ratio: 1/1;
      background: transparent;
    }
    .variant-shuffle-skeleton .skeleton-wishlist {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 32px;
      height: 32px;
      background: rgba(255,255,255,0.9);
      border-radius: 50%;
      z-index: 10;
    }
    .variant-shuffle-skeleton .skeleton-badge {
      position: absolute;
      top: 8px;
      left: 8px;
      width: 50px;
      height: 22px;
      background: rgba(0,0,0,0.06);
      border-radius: 4px;
      z-index: 10;
    }
    .variant-shuffle-skeleton .skeleton-title {
      height: 18px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 10px;
      width: 85%;
    }
    .variant-shuffle-skeleton .skeleton-price {
      height: 16px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.5s infinite;
      border-radius: 4px;
      width: 50%;
    }
    @keyframes skeleton-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .variant-shuffle-fade-in {
      animation: variant-fade-in 0.3s ease-out;
    }
    @keyframes variant-fade-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    /* Force product card titles to 1 line */
    .product-card__title,
    .product-card__title .reversed-link__text {
      display: -webkit-box !important;
      -webkit-line-clamp: 1 !important;
      -webkit-box-orient: vertical !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: normal !important;
      line-height: 1.4 !important;
      max-height: 1.4em !important;
    }
  `;
  document.head.appendChild(style);
})();
class VariantShuffle {
  constructor() {
    this.initialized = false;
    this.storageKey = 'variant_shuffle_seed';
    this.init();
  }

  // Create skeleton card HTML - uses theme classes for consistent styling
  createSkeletonCard() {
    var _window$FoxThemeSetti;
    return `
      <div class="product-card variant-shuffle-skeleton product-card-style-${((_window$FoxThemeSetti = window.FoxThemeSettings) === null || _window$FoxThemeSetti === void 0 ? void 0 : _window$FoxThemeSetti.pcard_style) || 'standard'}">
        <div class="product-card__wrapper h-full">
          <div class="product-card__image-wrapper">
            <div class="product-card__image" style="--aspect-ratio: 1"></div>
            <div class="skeleton-wishlist"></div>
            <div class="skeleton-badge"></div>
          </div>
          <div class="product-card__info">
            <div class="skeleton-title"></div>
            <div class="skeleton-price"></div>
          </div>
        </div>
      </div>
    `;
  }

  // Show skeleton placeholders in grid
  showSkeletons(grid, count, isSlider) {
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const wrapper = document.createElement('div');
      wrapper.className = isSlider ? 'f-column swiper-slide' : 'f-column product-column';
      wrapper.innerHTML = this.createSkeletonCard();
      fragment.appendChild(wrapper);
    }
    grid.innerHTML = '';
    grid.appendChild(fragment);
  }
  init() {
    if (this.initialized) return;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.process();
      });
    } else {
      this.process();
    }

    // Listen for tab changes to process newly added grids (for featured collection tabs)
    document.addEventListener('tabChange', e => {
      var _e$detail;
      const newTab = (_e$detail = e.detail) === null || _e$detail === void 0 ? void 0 : _e$detail.selectedTab;
      if (newTab) {
        // Find grids in the newly active tab that haven't been processed
        const grids = newTab.querySelectorAll('[data-variant-shuffle-grid]:not([data-variant-shuffle-processed])');
        grids.forEach(grid => this.processGrid(grid));
      }
    });
    this.initialized = true;
  }

  // Get or create a consistent seed for shuffling (persists for the session)
  getSeed() {
    let seed = sessionStorage.getItem(this.storageKey);
    if (!seed) {
      // Use current date as seed so shuffle stays same for the day
      const today = new Date();
      seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
      sessionStorage.setItem(this.storageKey, seed);
    }
    return parseInt(seed);
  }

  // Seeded random number generator for consistent shuffling
  seededRandom(seed) {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  // Shuffle array with consistent seed
  shuffleArray(array, seed) {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(this.seededRandom(currentSeed) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      currentSeed++;
    }

    // De-cluster: ensure same-product variants aren't adjacent
    return this.decluster(shuffled);
  }

  // Move adjacent same-product variants apart
  decluster(items) {
    const result = [...items];
    for (let i = 0; i < result.length - 1; i++) {
      if (result[i].handle && result[i].handle === result[i + 1].handle) {
        // Find the nearest item with a different handle to swap with
        for (let j = i + 2; j < result.length; j++) {
          if (result[j].handle !== result[i].handle) {
            [result[i + 1], result[j]] = [result[j], result[i + 1]];
            break;
          }
        }
      }
    }
    return result;
  }

  // Check if sort order should be preserved (collection pages always have a sort order)
  isCollectionGrid(grid) {
    return grid && grid.id === 'ProductsList';
  }
  shouldSkipShuffle(grid) {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    // Skip shuffle if sort_by is explicitly in URL
    if (sortBy !== null && sortBy !== '') return true;
    // Skip shuffle on collection pages (they always have a default sort like best-selling)
    if (this.isCollectionGrid(grid)) return true;
    return false;
  }

  // Spread variants of the same product apart while preserving overall sort order.
  // Round-robins through product groups so variant 1 of each product comes first
  // (in best-selling order), then variant 2 of each, etc.
  spreadVariants(items) {
    const groups = [];
    const groupMap = new Map();
    for (const item of items) {
      const key = item.handle || '';
      if (!groupMap.has(key)) {
        const group = [];
        groupMap.set(key, group);
        groups.push(group);
      }
      groupMap.get(key).push(item);
    }
    const result = [];
    let round = 0;
    let hasMore = true;
    while (hasMore) {
      hasMore = false;
      for (const group of groups) {
        if (round < group.length) {
          result.push(group[round]);
          hasMore = true;
        }
      }
      round++;
    }
    return result;
  }

  // Extract variant data from a product card
  extractProductData(productCard) {
    const productLink = productCard.querySelector('a[href*="/products/"]');
    if (!productLink) return null;
    const productUrl = productLink.getAttribute('href').split('?')[0];
    const productHandle = productUrl.split('/products/')[1];
    return {
      element: productCard,
      handle: productHandle,
      url: productUrl,
      html: productCard.outerHTML
    };
  }

  // Fetch product data including variants
  async fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Create a variant card by cloning and modifying the original product card
  createVariantCard(originalCard, product, variant) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(originalCard.html, 'text/html');
    const card = doc.querySelector('.product-card') || doc.body.firstElementChild;
    if (!card) return null;

    // Update product URL to include variant parameter
    const links = card.querySelectorAll('a[href*="/products/"]');
    links.forEach(link => {
      const href = link.getAttribute('href').split('?')[0];
      link.setAttribute('href', `${href}?variant=${variant.id}`);
    });

    // Update variant-specific forms
    const forms = card.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
    });

    // Determine the correct image for this variant
    // Use variant-specific image if available, otherwise fall back to product's featured image
    const imageUrl = variant.featured_image ? variant.featured_image.src : product.featured_image ? product.featured_image.src : null;

    // Update wishlist button data attributes for variant
    const wishlistBtn = card.querySelector('.wishlist-btn');
    if (wishlistBtn && variant) {
      wishlistBtn.setAttribute('data-variant-id', variant.id);
      if (imageUrl) {
        wishlistBtn.setAttribute('data-product-image', imageUrl);
      }
      wishlistBtn.setAttribute('data-product-price', variant.price);
      wishlistBtn.setAttribute('data-product-url', `/products/${product.handle}?variant=${variant.id}`);

      // Remove initialization flag so wishlist will re-attach event listeners
      wishlistBtn.removeAttribute('data-wishlist-initialized');
    }
    if (imageUrl) {
      // Build proper srcset with Shopify CDN size params
      const baseSrc = imageUrl.replace(/\?.*$/, '');
      const sizes = [165, 355, 535, 710, 1070];
      const srcset = sizes.map(w => `${baseSrc}?width=${w} ${w}w`).join(', ');

      // Update main image
      const mainImg = card.querySelector('.product-card__image--main img');
      if (mainImg) {
        mainImg.setAttribute('src', `${baseSrc}?width=535`);
        mainImg.setAttribute('srcset', srcset);
        if (mainImg.hasAttribute('data-src')) mainImg.setAttribute('data-src', `${baseSrc}?width=535`);
        if (mainImg.hasAttribute('data-srcset')) mainImg.setAttribute('data-srcset', srcset);
      }

      // Update secondary/hover image to match variant
      const secondImg = card.querySelector('.product-card__image--second img');
      if (secondImg) {
        secondImg.setAttribute('src', `${baseSrc}?width=535`);
        secondImg.setAttribute('srcset', srcset);
        if (secondImg.hasAttribute('data-src')) secondImg.setAttribute('data-src', `${baseSrc}?width=535`);
        if (secondImg.hasAttribute('data-srcset')) secondImg.setAttribute('data-srcset', srcset);
      }
    }

    // Update title if variant has a different title
    const titleElement = card.querySelector('.product-card__title .reversed-link__text, .product-card__title');
    if (titleElement && variant.name && variant.name !== product.title) {
      titleElement.textContent = variant.name;
    }

    // Update price
    const priceElement = card.querySelector('.price, [class*="price"]');
    if (priceElement && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);
      const priceDisplay = priceElement.querySelector('.price-item--regular, [class*="price"]');
      if (priceDisplay) {
        priceDisplay.textContent = formattedPrice;
      }
    }

    // Add a data attribute to identify this as a variant card
    card.setAttribute('data-variant-id', variant.id);
    card.setAttribute('data-product-id', product.id);
    card.setAttribute('data-product-handle', product.handle);
    return card.outerHTML;
  }

  // Format money — round to whole dirhams + "Dhs" to match the store's money
  // filter (avoids "15.00 MAD" next to the theme's "15 Dhs")
  formatMoney(cents) {
    const amount = Math.round((cents || 0) / 100).toLocaleString('fr-FR');
    return `${amount} Dhs`;
  }

  // Process all product grids on the page
  async process() {
    // Find product grids - updated selectors to match theme structure
    const productGrids = document.querySelectorAll('#ProductsList, #product-grid, .products-list, .featured-collection__items, .collection__product-grid, .product-grid, [data-products-grid]');
    if (productGrids.length === 0) {
      return;
    }
    for (const grid of productGrids) {
      await this.processGrid(grid);
    }
  }
  async processGrid(grid) {
    var _grid$parentElement;
    // Mark as processed to prevent re-processing
    if (grid.hasAttribute('data-variant-shuffle-processed')) return;
    grid.setAttribute('data-variant-shuffle-processed', 'true');

    // Check if this is a slider/swiper grid
    const isSlider = grid.classList.contains('swiper-wrapper');

    // Check if this grid has server-rendered skeletons
    const hasServerSkeletons = grid.hasAttribute('data-variant-shuffle-grid');

    // Look for hidden source container with actual products
    const sourceContainer = (_grid$parentElement = grid.parentElement) === null || _grid$parentElement === void 0 ? void 0 : _grid$parentElement.querySelector('[data-variant-shuffle-source]');
    let productCards;
    let originalGridHTML;
    let tempContainer;
    if (hasServerSkeletons && sourceContainer) {
      // Server-rendered skeletons: extract products from hidden source
      productCards = Array.from(sourceContainer.querySelectorAll('.product-column, .f-column')).filter(el => {
        return el.querySelector('.product-card, [class*="product-card"]');
      });
      if (productCards.length === 0) {
        return;
      }

      // Skeletons already shown, no need to show again
      originalGridHTML = sourceContainer.innerHTML;
      tempContainer = sourceContainer;
    } else {
      // Original behavior: extract from grid itself
      productCards = Array.from(grid.querySelectorAll('.product-column, .f-column')).filter(el => {
        return el.querySelector('.product-card, [class*="product-card"]');
      });
      if (productCards.length === 0) {
        return;
      }

      // Store original grid content as fallback
      originalGridHTML = grid.innerHTML;

      // Skip skeleton display for collection pages - products are already visible
      // Showing skeletons would cause a jarring flash effect

      // Create a temporary container to extract data without affecting visible DOM
      tempContainer = document.createElement('div');
      tempContainer.innerHTML = originalGridHTML;
    }
    const seed = this.getSeed();
    const variantItems = [];
    const nonProductItems = [];

    // Preserve non-product items (like image cards, banners) from the main grid
    Array.from(grid.children).forEach((child, index) => {
      const isProductColumn = child.classList.contains('product-column') || child.classList.contains('f-column');
      const hasProductCard = child.querySelector('.product-card, [class*="product-card"]');
      const isSkeleton = child.querySelector('.variant-shuffle-skeleton');
      if (!isProductColumn || !hasProductCard && !isSkeleton) {
        nonProductItems.push({
          element: child.cloneNode(true),
          position: index
        });
      }
    });

    // Extract all product data from temp/source container
    const tempProductCards = Array.from(tempContainer.querySelectorAll('.product-column, .f-column')).filter(el => {
      return el.querySelector('.product-card:not(.variant-shuffle-skeleton), [class*="product-card"]:not(.variant-shuffle-skeleton)');
    });
    const productDataList = tempProductCards.map(card => this.extractProductData(card)).filter(data => data !== null);
    if (productDataList.length === 0) {
      // Restore original content if no products found
      if (!hasServerSkeletons) {
        grid.innerHTML = originalGridHTML;
      }
      return;
    }

    // Fetch all products in parallel for better performance
    const productPromises = productDataList.map(data => this.fetchProductData(data.handle).then(product => ({
      productData: data,
      product
    })));
    const results = await Promise.all(productPromises);

    // Process results and create variant cards
    for (const {
      productData,
      product
    } of results) {
      if (!product || !product.variants || product.variants.length <= 1) {
        // No variants or only one variant, keep original card
        variantItems.push({
          html: productData.html,
          handle: productData.handle,
          sortKey: Math.random()
        });
        continue;
      }

      // Create a card for each available variant
      for (const variant of product.variants) {
        if (!variant.available) continue;
        const variantCard = this.createVariantCard(productData, product, variant);
        if (variantCard) {
          variantItems.push({
            html: variantCard,
            handle: product.handle,
            sortKey: Math.random()
          });
        }
      }
    }
    if (variantItems.length === 0) {
      // Restore original content if no variants found
      grid.innerHTML = originalGridHTML;
      return;
    }

    // Preserve admin sort order, spread same-product variants apart, then decluster any remaining adjacents
    const itemsToRender = this.decluster(this.spreadVariants(variantItems));

    // Use DocumentFragment to batch DOM operations (prevents forced reflows)
    const fragment = document.createDocumentFragment();
    itemsToRender.forEach(item => {
      const wrapper = document.createElement('div');
      wrapper.className = isSlider ? 'f-column swiper-slide' : 'f-column product-column';
      wrapper.setAttribute('data-variant-processed', 'true');
      wrapper.innerHTML = item.html;
      // Add fade-in animation class to the product card
      const card = wrapper.querySelector('.product-card');
      if (card) {
        card.classList.add('variant-shuffle-fade-in');
      }
      fragment.appendChild(wrapper);
    });

    // Calculate insertion positions BEFORE clearing grid
    const insertionMap = nonProductItems.map(item => ({
      element: item.element,
      position: item.position
    }));

    // Single DOM write: clear and append fragment
    grid.innerHTML = '';
    grid.appendChild(fragment);

    // Re-insert non-product items at their original positions
    insertionMap.forEach(({
      element,
      position
    }) => {
      if (position < grid.children.length) {
        grid.insertBefore(element, grid.children[position]);
      } else {
        grid.appendChild(element);
      }
    });

    // Trigger any lazy loading or other initialization
    this.triggerReinitialization();
  }

  // Trigger re-initialization of any dynamic features
  triggerReinitialization() {
    // Dispatch custom event for other scripts to hook into
    window.dispatchEvent(new CustomEvent('variantShuffle:complete'));

    // Give the DOM a moment to settle before reinitializing
    setTimeout(() => {
      // Re-initialize common features
      if (window.FoxTheme && typeof window.FoxTheme.init === 'function') {
        try {
          window.FoxTheme.init();
        } catch (e) {}
      }

      // Re-initialize Swiper instances for featured collections
      document.querySelectorAll('featured-collection').forEach(fc => {
        if (fc.swiper) {
          try {
            fc.swiper.update();
            fc.swiper.slideTo(0);
          } catch (e) {}
        }
      });

      // Re-initialize native wishlist
      if (window.wishlist && typeof window.wishlist.attachEventListeners === 'function') {
        try {
          window.wishlist.attachEventListeners();
          window.wishlist.updateAllHearts();
        } catch (e) {}
      }

      // Re-trigger lazy loading if present
      if ('loading' in HTMLImageElement.prototype) {
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (img.loading === 'lazy') {
            img.loading = 'lazy';
          }
        });
      }
    }, 100);
  }

  // Process newly appended items in an already-processed grid (for load-more)
  async processNewItems(grid) {
    if (!grid) return;

    // Find items that haven't been variant-processed yet
    const newItems = Array.from(grid.querySelectorAll('.f-column:not([data-variant-processed]), .product-column:not([data-variant-processed])')).filter(el => {
      return el.querySelector('.product-card:not(.variant-shuffle-skeleton)');
    });
    if (newItems.length === 0) return;
    const variantItems = [];
    for (const item of newItems) {
      // Mark as being processed
      item.setAttribute('data-variant-processed', 'true');
      const productData = this.extractProductData(item);
      if (!productData) continue;
      const product = await this.fetchProductData(productData.handle);
      if (!product || !product.variants || product.variants.length <= 1) {
        // Keep original card, no expansion needed
        continue;
      }

      // Create variant cards for this product
      const newVariantCards = [];
      for (const variant of product.variants) {
        if (!variant.available) continue;
        const variantCard = this.createVariantCard(productData, product, variant);
        if (variantCard) {
          newVariantCards.push({
            html: variantCard,
            handle: product.handle
          });
        }
      }
      if (newVariantCards.length > 0) {
        // Replace the original item with its first variant card
        item.innerHTML = newVariantCards[0].html;

        // Insert remaining variant cards after the original, spread apart
        const remaining = newVariantCards.slice(1);
        let insertAfter = item;
        for (const vc of remaining) {
          const wrapper = document.createElement('div');
          wrapper.className = item.className;
          wrapper.setAttribute('data-variant-processed', 'true');
          wrapper.innerHTML = vc.html;
          const card = wrapper.querySelector('.product-card');
          if (card) card.classList.add('variant-shuffle-fade-in');
          insertAfter.parentNode.insertBefore(wrapper, insertAfter.nextSibling);
          // Skip some positions to spread variants apart
          let next = wrapper.nextElementSibling;
          if (next) insertAfter = next;else insertAfter = wrapper;
        }
      }
    }
    this.triggerReinitialization();
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.VariantShuffle = VariantShuffle;
  const instance = new VariantShuffle();
  window.variantShuffleInstance = instance;
}
